import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import useCreateStripeAccountLink from "@/Apollo/hooks/useCreateAccountLink";
import { ACCOUNT_SETTINGS_ROUTE } from "@/common/static/routes.static";
import {
  PAYMENT_COPY,
  STRIPE_REFRESH_PARAM,
  STRIPE_RETURN_PARAM,
  paymentLokaliseKey,
} from "@/components/Account/payment/payment.static";
import { StripeLinkType } from "@/components/Account/payment/payment.utils";
import addToast from "@/toast/addToast";

/** Where the page remembers the status it left with, to notice the change on return. */
export const STRIPE_RETURN_BASELINE_KEY =
  "whoppah.payment-settings.stripe-baseline";

export type StripeReturnBaseline = {
  status: string;
  asOf: string | null;
};

export const readStripeReturnBaseline = (): StripeReturnBaseline | null => {
  try {
    const raw = window.sessionStorage.getItem(STRIPE_RETURN_BASELINE_KEY);

    return raw ? (JSON.parse(raw) as StripeReturnBaseline) : null;
  } catch {
    return null;
  }
};

export const clearStripeReturnBaseline = () => {
  try {
    window.sessionStorage.removeItem(STRIPE_RETURN_BASELINE_KEY);
  } catch {
    // Storage can be unavailable; the page then compares across polls.
  }
};

const writeStripeReturnBaseline = (baseline: StripeReturnBaseline | null) => {
  if (!baseline) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      STRIPE_RETURN_BASELINE_KEY,
      JSON.stringify(baseline)
    );
  } catch {
    // Same as above.
  }
};

/**
 * Mints a single-use Account Link and sends the browser to Stripe. Both
 * return_url and refresh_url point at the Payment settings page, wherever
 * the button sits: `?stripe_return=1` starts the status polling there,
 * `?stripe_refresh=1` re-mints an expired link. A failure is said out loud;
 * the old button failed silently.
 */
export const useStripeRedirect = () => {
  const { t } = useTranslation();
  const { locale, defaultLocale } = useRouter();
  const [createStripeAccountLink] = useCreateStripeAccountLink();
  const [redirecting, setRedirecting] = useState(false);

  // Leaving for Stripe keeps the button in its loading state. A seller who
  // closes Stripe's page and comes back with the browser's back button gets
  // this page restored from the back/forward cache, state included, so the
  // spinner would never stop. pageshow with `persisted` is that restore.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setRedirecting(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const redirect = useCallback(
    async (
      linkType: StripeLinkType,
      baseline: StripeReturnBaseline | null = null
    ) => {
      setRedirecting(true);

      try {
        const prefix = locale && locale !== defaultLocale ? `/${locale}` : "";
        const page = `${window.location.origin}${prefix}${ACCOUNT_SETTINGS_ROUTE}/payment`;
        const response = await createStripeAccountLink({
          variables: {
            input: {
              type: linkType,
              return_url: `${page}?${STRIPE_RETURN_PARAM}=1`,
              refresh_url: `${page}?${STRIPE_REFRESH_PARAM}=1`,
            },
          },
        });
        const url: string | undefined =
          response?.data?.createStripeAccountLink?.url;

        if (!url) {
          throw new Error("Stripe account link came back without a url");
        }

        writeStripeReturnBaseline(baseline);
        window.location.assign(url);
      } catch (error) {
        setRedirecting(false);
        addToast(
          t(paymentLokaliseKey("linkError"), PAYMENT_COPY.linkError.nl),
          { appearance: "error" }
        );
      }
    },
    [createStripeAccountLink, defaultLocale, locale, t]
  );

  return { redirect, redirecting };
};
